import * as React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { LanguageContext } from '../../utilities/LocalizationModule';
import {
    Label,
    CommandBar,
    ICommandBarItemProps,
    Stack,
    FontSizes,
    FontWeights,
    ILabelStyles,
    ScrollablePane,
    ScrollbarVisibility,
    SearchBox,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    MessageBarButton,
    Pivot,
    PivotItem,
} from '@fluentui/react';
import { RootState } from '../../redux';
import { connect, ConnectedProps } from 'react-redux';
import AddSubscriptionPanel from '../panels/AddSubscriptionPanel';
import OrphanCustomersPanel from '../panels/OrphanCustomersPanel';
import ManageStripeProductsPanel from '../panels/ManageStripeProductsPanel';
import { assignAllUsersToSubscription, cancelSubscription, createStripePortalSession, getMultiTenantSubscriptions, getOrphanCustomersCount, getSubscriptionById, getTenantSubscriptions, previewStripeSubscriptionQuantity, reactivateSubscription, revokeAllUsersFromSubscription, updateInvoiceSubscriptionQuantity, updateStripeSubscriptionQuantity, updateSubscriptionStatus } from '../../utilities/helpers/ApiHelper';
import { useSubscriptionsHubConnection } from '../../hooks/useSubscriptionsHubConnection';
import { upsertSubscription } from '../../utilities/helpers/SubscriptionMerge';
import SubscriptionsList from '../SubscriptionsList';
import SelectTenantPanel from '../panels/SelectTenantPanel';
import { AssignSubscriptionRequest, ITenant, ITenantUser, MultiTenantSubscriptionRequest, SubscriptionStatusUpdateRequest } from '../../data-structures/interfaces';
import { labelHeadingStyle } from '../../styles/PanelStyle';
import { SubscriptionStatusEnum, SubscriptionTierEnum } from '../../data-structures/enums';
import { getUserLicenseType } from '../../utilities/helpers/UserLicenseTypeHelper';
import { updateUser } from '../../redux/modules/user';
import * as UserTableStyle from "../../styles/UserTableStyle"
import SubscriptionsExportPanel from '../panels/SubscriptionsExportPanel';
import SubscriptionHistoryPanel from '../panels/SubscriptionHistoryPanel';
import SubscriptionPaymentsPanel from '../panels/SubscriptionPaymentsPanel';
import ConfirmDialog from '../ConfirmDialog';
import ChangeQuantityDialog from '../ChangeQuantityDialog';
import XLSX from 'xlsx-js-style';
import { filterSubscriptionsByDisplayView, getSubscriptionDisplayViewCounts, SubscriptionDisplayView } from '../../utilities/helpers/SubscriptionViewHelper';
import { isServiceProduct } from '../../utilities/helpers/SubscriptionCapacityHelper';
import {
    getSubscriptionSelectionCount,
    getSubscriptionSelectionLabel,
    shouldShowSubscriptionGlobalCommands,
    shouldShowSubscriptionSelectionCount
} from '../../utilities/helpers/SubscriptionCommandBarHelper';
import { releaseTransientCommandFocus } from '../../utilities/helpers/CommandBarSelectionHelper';
import { portalCommandBarSearchContainerStyle, portalCommandBarStyles, portalPivotStyles } from '../../styles/CommandBarStyle';
import { subscriptionContentStyle, subscriptionPageStyle, subscriptionTableContainerStyle } from '../../styles/SubscriptionLayoutStyle';

export const headingStyle: Partial<ILabelStyles> = {
    root: {
        fontWeight: FontWeights.semibold,
        fontSize: FontSizes.size28,
    }
}

const mapDispatchToProps = {
    updateUser
}

const mapStateToProps = (state: RootState) => {
    return {
        tenants: state.user.tenants,
        users: state.user.users,
        isGlobalAdmin: state.user.isGlobalAdmin,
        account: state.user.account
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type SubscriptionsProps = PropsFromRedux;

// Resolves the tenant the page should show from the URL :tenantId param. Single-tenant users
// always fall back to their sole tenant (so /subscriptions, and even a bad/foreign id, still
// show it); multi-tenant users with no match get undefined => the "all tenants" view.
const resolveTenant = (id: string | undefined, list: ITenant[]): ITenant | undefined => {
    if (!list || list.length === 0) return undefined;
    if (id) return list.find(t => String(t.id) === id) ?? (list.length === 1 ? list[0] : undefined);
    return list.length === 1 ? list[0] : undefined;
};

const Subscriptions: React.FC<SubscriptionsProps> = ({
    tenants,
    users,
    updateUser,
    isGlobalAdmin,
    account
}: SubscriptionsProps) => {

    const languageStrings = React.useContext(LanguageContext);

    const { tenantId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isAddSubscriptionPanelOpen, setIsAddSubscriptionPanelOpen] = React.useState(false);
    const [isSelectTenantPanelOpen, setIsSelectTenantPanelOpen] = React.useState(false);
    const [isExportPanelOpen, setIsExportPanelOpen] = React.useState(false);
    const [subscriptions, setSubscriptions] = React.useState<any[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [selectedTenant, setSelectedTenant] = React.useState<ITenant>(() => resolveTenant(tenantId, tenants));
    // Stripe Checkout return marker (?checkout=success|cancelled). Copied to local state so the
    // banner persists after we strip the query param from the URL.
    const [checkoutResult, setCheckoutResult] = React.useState<string | null>(null);
    const [filterValue, setFilterValue] = React.useState<string>();
    const [subscriptionView, setSubscriptionView] = React.useState<SubscriptionDisplayView>(SubscriptionDisplayView.Current);

    const [selectedItems, setSelectedItems] = React.useState<any[]>([]);
    const [isBusy, setIsBusy] = React.useState(false);
    const [isExporting, setIsExporting] = React.useState<boolean>(false);
    const [isRefreshing, setIsRefreshing] = React.useState(false);
    const [deselectAllSubscriptions, setDeselectAllSubscriptions] = React.useState(false);

    const [quantityDialogItem, setQuantityDialogItem] = React.useState<any | null>(null);
    const [cancelDialog, setCancelDialog] = React.useState<{ items: any[]; message: string } | null>(null);
    const [reactivateDialog, setReactivateDialog] = React.useState<{ item: any; message: string } | null>(null);
    const [historyPanelSubId, setHistoryPanelSubId] = React.useState<number | null>(null);
    const [paymentsPanelSubId, setPaymentsPanelSubId] = React.useState<number | null>(null);

    // Orphan customers (Stripe customers whose webhook events couldn't be linked to a tenant).
    // Surfaced to global admins via a far-right toolbar button + a banner that only shows when
    // count > 0. Cheap COUNT-only endpoint on mount; full list loads when the panel opens.
    const [orphanCount, setOrphanCount] = React.useState<number>(0);
    const [orphanBannerDismissed, setOrphanBannerDismissed] = React.useState<boolean>(false);
    const [isOrphanPanelOpen, setIsOrphanPanelOpen] = React.useState<boolean>(false);

    // Manage Stripe Products (admin catalog). The nonce is bumped whenever a SignalR
    // StripeCatalogChanged event arrives — the panel watches this and refetches the
    // catalog so another admin's edit reflects without a manual refresh.
    const [isManageStripeProductsPanelOpen, setIsManageStripeProductsPanelOpen] = React.useState<boolean>(false);
    const [stripeCatalogNonce, setStripeCatalogNonce] = React.useState<number>(0);

    const refreshOrphanCount = React.useCallback(async () => {
        if (!isGlobalAdmin) return;
        try {
            const result = await getOrphanCustomersCount();
            if (result && typeof result.count === 'number') {
                setOrphanCount(result.count);
            }
        } catch {
            // Non-fatal: if the count endpoint fails the badge stays at its last value.
        }
    }, [isGlobalAdmin]);

    React.useEffect(() => {
        refreshOrphanCount();
    }, [refreshOrphanCount]);

    // Pending-quantity overlay: shows "X -> Y" in the licenseCount cell after an admin
    // commits a quantity change, until the corresponding webhook lands and the live row's
    // licenseCount catches up. The 2.5 s targeted poller that previously drove this has been
    // replaced by the realtime push pipeline; the badge now dismisses when the SignalR
    // push triggers a per-row refetch that returns a row with the matching licenseCount.
    const [pendingQuantityChanges, setPendingQuantityChanges] = React.useState<Record<number, number>>({});
    const refreshSubscriptionsRef = React.useRef<() => Promise<any[]>>(async () => []);

    const fetchSubscrptionsAndPayments = async (): Promise<any[]> => {
        const subscriptionsRes = await getTenantSubscriptions(selectedTenant.id, languageStrings, tenants);
        const sorted = subscriptionsRes.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
        setSubscriptions(sorted);
        return sorted;
    }

    const fetchMultiTenantSubscrptions = async (): Promise<any[]> => {
        const request: MultiTenantSubscriptionRequest = {
            tenantIds: tenants.map(x => x.id),
            userProfile: {
                accountId: account?.homeAccountId,
                username: account?.username
            }
        }

        const multiSubscriptions = await getMultiTenantSubscriptions(request, tenants, languageStrings);
        const sorted = multiSubscriptions
            .sort((a, b) => {
                if (a.tenantName < b.tenantName) return -1;
                if (a.tenantName > b.tenantName) return 1;
                return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
            });
        setSubscriptions(sorted);
        return sorted;
    }

    // Re-bind the refresh closure on every commit so the ref always reflects the latest
    // captures of selectedTenant, languageStrings, tenants. Used by the Refresh toolbar
    // button AND by the realtime hub's onReady / onReconnected callback.
    React.useEffect(() => {
        refreshSubscriptionsRef.current = async (): Promise<any[]> => {
            if (selectedTenant) {
                return await fetchSubscrptionsAndPayments();
            } else if (!selectedTenant && tenants?.length > 1) {
                return await fetchMultiTenantSubscrptions();
            }
            return [];
        };
    });

    const addPendingQuantityChange = React.useCallback((subId: number, requestedQty: number) => {
        setPendingQuantityChanges(prev => ({ ...prev, [subId]: requestedQty }));
    }, []);

    // Clears a pending-quantity badge if the freshly-fetched row's licenseCount has caught up.
    const reconcilePendingFor = React.useCallback((freshRow: any) => {
        setPendingQuantityChanges(prev => {
            const requested = prev[freshRow.id];
            if (requested == null) return prev;
            if (freshRow.licenseCount === requested) {
                const next = { ...prev };
                delete next[freshRow.id];
                return next;
            }
            return prev;
        });
    }, []);

    // ---- Realtime push wiring ----
    // Burst-coalesce push events: every SubscriptionChanged is added to a Set; a single
    // rAF-scheduled drain fetches all affected rows in one batch and upserts them.
    const pendingFetchIdsRef = React.useRef<Set<number>>(new Set());
    const drainScheduledRef = React.useRef<number | null>(null);
    // Set while handleHubReady's full refetch is awaiting its HTTP response. While true,
    // incoming SubscriptionChanged events still queue ids but do NOT schedule a drain —
    // we'd race the full refetch's setState and potentially serve stale snapshot data.
    // The drain is kicked off after the full refetch resolves.
    const fullRefetchInFlightRef = React.useRef<boolean>(false);
    // Synchronously-updated ref of the latest selectedTenant. Setting refs during render
    // is allowed by React and avoids the brief lag of an effect-based ref where the
    // signalr callback could read the stale value between render and effect commit.
    const selectedTenantRef = React.useRef(selectedTenant);
    selectedTenantRef.current = selectedTenant;

    const drainPendingFetches = React.useCallback(async () => {
        drainScheduledRef.current = null;
        const ids = Array.from(pendingFetchIdsRef.current);
        pendingFetchIdsRef.current.clear();
        if (ids.length === 0) return;

        const rows = await Promise.all(ids.map(id => getSubscriptionById(id, languageStrings, tenants)));
        setSubscriptions(prev => {
            let next = prev;
            for (const row of rows) {
                if (!row) continue;
                next = upsertSubscription(next, row);
                reconcilePendingFor(row);
            }
            return next;
        });
    }, [languageStrings, tenants, reconcilePendingFor]);

    const handleSubscriptionChanged = React.useCallback((evt: {
        subscriptionId: number; tenantId: number; kind: string; occurredAt: string;
    }) => {
        // Defensive client-side scope filter: if a stale event arrives mid-scope-change
        // (between LeaveAsync(old) and JoinAsync(new)), drop it.
        const currentTenant = selectedTenantRef.current;
        if (currentTenant && evt.tenantId !== currentTenant.id) return;

        pendingFetchIdsRef.current.add(evt.subscriptionId);
        // Don't schedule a drain while the full refetch is in flight; we'd race its
        // setState and risk losing the update. The handleHubReady tail will drain
        // whatever's queued once the refetch completes.
        if (fullRefetchInFlightRef.current) return;
        if (drainScheduledRef.current == null) {
            drainScheduledRef.current = window.requestAnimationFrame(() => { drainPendingFetches(); });
        }
    }, [drainPendingFetches]);

    // onReady fires on initial connect, scope change, and reconnect. Drives a full list
    // refetch to close the "events arriving between start and join" race window. Clears
    // pending per-row fetches so the full result becomes the authoritative state. Awaits
    // the refetch (and only afterward drains any events that arrived DURING the refetch).
    const handleHubReady = React.useCallback(async () => {
        pendingFetchIdsRef.current.clear();
        if (drainScheduledRef.current != null) {
            window.cancelAnimationFrame(drainScheduledRef.current);
            drainScheduledRef.current = null;
        }
        fullRefetchInFlightRef.current = true;
        try {
            await refreshSubscriptionsRef.current?.();
        } finally {
            fullRefetchInFlightRef.current = false;
        }
        // Drain anything that arrived during the refetch — those rows may be newer than
        // the snapshot the refetch returned. The per-row fetch reads the current DB state
        // and upserts via setSubscriptions(prev => ...), which composes correctly on top
        // of the refetch's setState.
        if (pendingFetchIdsRef.current.size > 0 && drainScheduledRef.current == null) {
            drainScheduledRef.current = window.requestAnimationFrame(() => { drainPendingFetches(); });
        }
    }, [drainPendingFetches]);

    // Mirror the hub's connection state into a ref so the selectedTenant effect can decide
    // whether to perform the "always works" immediate fetch. When the hub is Connected, its
    // scope-change handler will fire onReady → handleHubReady → refetch, so the immediate
    // fetch would just duplicate that work. When the hub is Connecting / Reconnecting /
    // Disconnected, onReady may not fire promptly (or at all), so we still need the
    // immediate fetch as a fallback. Default Disconnected so first-mount keeps the fetch.
    const hubStateRef = React.useRef<string>('Disconnected');
    const handleConnectionStateChange = React.useCallback((state: string) => {
        hubStateRef.current = state;
        if (state === 'Disconnected') {
            console.warn('[realtime] subscriptions hub disconnected — list may be stale until reconnect');
        }
    }, []);

    // Compute the hub scope from the current view. View-driven, not permission-driven:
    // - Single tenant: tenant:{id}
    // - Multi-tenant global admin: admin:global (server filters to all tenants)
    // - Multi-tenant partner: explicit list of tenant:{id} for each accessible tenant
    const hubScope = React.useMemo(() => {
        if (selectedTenant) {
            return { tenantIds: [selectedTenant.id], joinAdminGlobal: false };
        }
        if (isGlobalAdmin) {
            return { tenantIds: [] as number[], joinAdminGlobal: true };
        }
        return { tenantIds: tenants.map(t => t.id), joinAdminGlobal: false };
    }, [selectedTenant, isGlobalAdmin, tenants]);

    useSubscriptionsHubConnection({
        tenantIds: hubScope.tenantIds,
        joinAdminGlobal: hubScope.joinAdminGlobal,
        onChanged: handleSubscriptionChanged,
        onCatalogChanged: () => setStripeCatalogNonce(n => n + 1),
        onReady: handleHubReady,
        onConnectionStateChange: handleConnectionStateChange,
    });

    // Unmount cleanup for the realtime push pipeline. Cancels any pending rAF drain and
    // clears the queued sub-ids so a late-firing rAF callback can't trigger an HTTP
    // request + setState on an unmounted component. The hook itself handles SignalR
    // teardown (LeaveAsync + stop) separately.
    React.useEffect(() => {
        return () => {
            if (drainScheduledRef.current != null) {
                window.cancelAnimationFrame(drainScheduledRef.current);
                drainScheduledRef.current = null;
            }
            pendingFetchIdsRef.current.clear();
        };
    }, []);

    const itemsWithPending = React.useMemo(() => {
        if (Object.keys(pendingQuantityChanges).length === 0) return subscriptions;
        return subscriptions.map(s => {
            const reqQty = pendingQuantityChanges[s.id];
            if (reqQty != null && s.licenseCount !== reqQty) {
                return { ...s, _pendingQuantityChange: reqQty };
            }
            return s;
        });
    }, [subscriptions, pendingQuantityChanges]);

    const subscriptionViewCounts = React.useMemo(() => {
        return getSubscriptionDisplayViewCounts(itemsWithPending);
    }, [itemsWithPending]);

    const visibleSubscriptions = React.useMemo(() => {
        return filterSubscriptionsByDisplayView(itemsWithPending, subscriptionView);
    }, [itemsWithPending, subscriptionView]);

    React.useEffect(() => {
        if (Array.isArray(subscriptions) && subscriptions.length > 0 && Array.isArray(selectedItems) && selectedItems.length === 1) {
            const sub = subscriptions.find(x => x.id === selectedItems[0].id);
            if (sub && JSON.stringify(sub) !== JSON.stringify(selectedItems[0])) {
                setSelectedItems([sub]);
            }
        }
    }, [subscriptions, selectedItems]);

    // URL is the source of truth for the selected tenant. Resolve :tenantId -> tenant on
    // param/tenants change. This ONLY sets state — the [selectedTenant] effect below remains
    // the sole owner of the reset+fetch+hub-scope. The identity guard avoids re-triggering
    // that effect when the tenants array reference changes for unrelated reasons (e.g. the
    // many updateUser dispatches this component makes), which would otherwise wipe the user's
    // filter/selection mid-task. Replaces the old single-tenant auto-select effect.
    React.useEffect(() => {
        if (!tenants || tenants.length === 0) return;
        const resolved = resolveTenant(tenantId, tenants);
        setSelectedTenant(prev => (prev?.id === resolved?.id ? prev : resolved));
    }, [tenantId, tenants]);

    // Surface the Stripe Checkout return, then strip the ?checkout query param (keeping the
    // :tenantId path) so a refresh / shared link doesn't re-show the banner.
    React.useEffect(() => {
        const c = searchParams.get('checkout');
        if (!c) return;
        setCheckoutResult(c);
        const next = new URLSearchParams(searchParams);
        next.delete('checkout');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    React.useEffect(() => {
        if (selectedTenant) {
            setFilterValue(null);
            setSubscriptionView(SubscriptionDisplayView.Current);
            setSelectedItems([]);
            setDeselectAllSubscriptions(prev => !prev);
            setSubscriptions([]);
            // Skip the immediate fetch when the hub is Connected — its scope-change effect
            // will fire LeaveAsync(old)/JoinAsync(new) → onReady → handleHubReady, which
            // performs the authoritative refetch in ~100-200ms. Falling through here would
            // duplicate that work on every tenant switch.
            //
            // When the hub is NOT Connected (initial mount, mid-reconnect, terminal
            // disconnect), keep the immediate fetch as the "always works" fallback so the
            // page renders data regardless of SignalR health. onReady will still fire if
            // the hub eventually connects, and its refetch composes correctly on top.
            if (hubStateRef.current !== 'Connected') {
                fetchSubscrptionsAndPayments();
            }
        } else if (!selectedTenant && tenants?.length > 1) {
            setSubscriptionView(SubscriptionDisplayView.Current);
            setSelectedItems([]);
            setDeselectAllSubscriptions(prev => !prev);
            fetchMultiTenantSubscrptions();
        }
    }, [selectedTenant]);

    const onSubscriptionViewChange = React.useCallback((item?: PivotItem) => {
        const view = item?.props.itemKey as SubscriptionDisplayView | undefined;
        if (!view || view === subscriptionView) return;

        setSubscriptionView(view);
        setSelectedItems([]);
        setDeselectAllSubscriptions(prev => !prev);
    }, [subscriptionView]);

    const selectionMenuItems = React.useMemo<ICommandBarItemProps[]>(() => {
        const items: ICommandBarItemProps[] = [];

        // --- Actions for a Single Selected Subscription ---
        if (selectedItems.length === 1) {
            const singleItem = selectedItems[0];
            const status = (singleItem.status as string)?.toLowerCase();
            const expiryDate = singleItem.expiryDateRaw ? new Date(singleItem.expiryDateRaw) : null;
            const isExpired = expiryDate && expiryDate < new Date();

            const canAssign = (status === SubscriptionStatusEnum.Active || (status === SubscriptionStatusEnum.Canceled && expiryDate && expiryDate >= new Date()));

            items.push(
                {
                    key: "assignall",
                    text: languageStrings.AssignAllLicenses,
                    iconProps: { iconName: "UserFollowed" },
                    disabled: isBusy || singleItem.availableLicenses === 0 || isExpired || !canAssign || isServiceProduct(singleItem),
                    onClick: () => {
                        setIsBusy(true);
                        assignAll(singleItem);
                    }
                },
                {
                    key: "revoke",
                    text: languageStrings.RevokeLicenses,
                    iconProps: { iconName: "UserRemove" },
                    disabled: isBusy || !singleItem.assignedLicenses || singleItem.assignedLicenses === "-",
                    onClick: () => {
                        setIsBusy(true);
                        revokeAll(selectedItems);
                    }
                },
                {
                    key: "history",
                    text: languageStrings.SubscriptionHistory ?? "History",
                    iconProps: { iconName: "History" },
                    disabled: isBusy,
                    onClick: () => setHistoryPanelSubId(singleItem.id)
                },
            );

            const isStripe = (singleItem.method as string)?.toLowerCase() === "stripe";
            if (isStripe) {
                items.push({
                    key: "payments",
                    text: languageStrings.Payments,
                    iconProps: { iconName: "PaymentCard" },
                    disabled: isBusy,
                    onClick: () => setPaymentsPanelSubId(singleItem.id)
                });

                items.push({
                    key: "manage-in-stripe",
                    text: languageStrings.ManageInStripe,
                    iconProps: { iconName: "Money" },
                    disabled: isBusy,
                    onClick: () => {
                        (async () => {
                            const result = await createStripePortalSession(singleItem.id);
                            if (result?.url) {
                                window.open(result.url, "_blank", "noopener,noreferrer");
                            }
                        })();
                    }
                });

                // Change quantity is technically accepted by Stripe on a sub with
                // cancel_at_period_end=true (it just prorates the remaining period before
                // the sub ends), but that's almost always not what the admin intends —
                // they'd see a stray proration charge for a sub that's about to disappear.
                // Hide the action until the admin reactivates; backend rejects the same case
                // as a 409 so a direct API call is also safe.
                if (status === SubscriptionStatusEnum.Active && !singleItem.cancelAtRaw && !isServiceProduct(singleItem)) {
                    items.push({
                        key: "change-quantity",
                        text: languageStrings.ChangeQuantity,
                        iconProps: { iconName: "NumberSymbol" },
                        disabled: isBusy,
                        onClick: () => {
                            setQuantityDialogItem(singleItem);
                        }
                    });
                }

                // Reactivate — visible only when a pending cancellation can still be reversed.
                // Mirrors Stripe's rule: "any time up to the end of the period". Once cancelAt
                // passes (and during the brief deletion-webhook race window), we hide rather
                // than show a button that's about to fail server-side.
                if (status === SubscriptionStatusEnum.Active && singleItem.cancelAtRaw && new Date(singleItem.cancelAtRaw) > new Date()) {
                    items.push({
                        key: "reactivate",
                        text: languageStrings.ReactivateSubscription,
                        iconProps: { iconName: "Play" },
                        disabled: isBusy,
                        onClick: () => {
                            const expiryText = new Date(singleItem.cancelAtRaw).toLocaleDateString();
                            const message = languageStrings.ReactivateStripeSubscriptionMessage.replace('{expiry}', expiryText);
                            setReactivateDialog({ item: singleItem, message });
                        }
                    });
                }
            }

            // Reactivate a cancelled non-Stripe subscription (trial or invoice). Marketplace is
            // excluded — Azure owns its lifecycle. Cancelling a non-Stripe sub is a local status
            // flip to Canceled (Method/ExpiryDate untouched — see CancelSubscription's
            // local-cancel path), so reactivating flips it back to Active. A trial carries an
            // ExpiryDate (its window) and renders as the grey "Cancels {date}" pill while it
            // lasts; once that date passes there's nothing left to resume, so we require a
            // future expiry for any dated row. Invoice subs have no ExpiryDate and can be
            // reactivated whenever cancelled. Stripe rows reactivate via the cancelAt-based
            // branch above. Gated on isGlobalAdmin to mirror who may cancel these in the first place.
            const method = (singleItem.method as string)?.toLowerCase();
            const isMarketplace = method?.startsWith("marketplace");
            const isTrial = singleItem.tier === SubscriptionTierEnum.Trial;
            const isInvoice = method === "invoice" || method === "invoice_static" || method === "invoice_dynamic";
            const datedTermStillValid = !expiryDate || expiryDate > new Date();
            if (isGlobalAdmin
                && !isStripe
                && !isMarketplace
                && (isTrial || isInvoice)
                && status === SubscriptionStatusEnum.Canceled
                && datedTermStillValid) {
                items.push({
                    key: "reactivate-local",
                    text: languageStrings.ReactivateSubscription,
                    iconProps: { iconName: "Play" },
                    disabled: isBusy,
                    onClick: () => {
                        const message = isTrial && expiryDate
                            ? (languageStrings.ReactivateTrialSubscriptionMessage as string).replace('{expiry}', expiryDate.toLocaleDateString())
                            : (languageStrings.ReactivateInvoiceSubscriptionMessage as string);
                        setReactivateDialog({ item: singleItem, message });
                    }
                });
            }

            // Change quantity for an invoice_static subscription. Only invoice_static carries a
            // finite seat count (LicenseCount); invoice_dynamic / legacy invoice are unlimited,
            // so there's no quantity to change. We additionally require a numeric licenseCount:
            // a null count renders as "unlimited" (see isUnlimitedSubscription), and offering a
            // "change quantity" dialog pre-filled with a misleading "1" for such a row would be
            // wrong. Unlike Stripe this is an immediate local update with no proration or cost
            // preview. Active rows only; gated on isGlobalAdmin.
            const isInvoiceStatic = method === "invoice_static";
            if (isGlobalAdmin
                && isInvoiceStatic
                && typeof singleItem.licenseCount === "number"
                && status === SubscriptionStatusEnum.Active
                && !isServiceProduct(singleItem)) {
                items.push({
                    key: "change-quantity-invoice",
                    text: languageStrings.ChangeQuantity,
                    iconProps: { iconName: "NumberSymbol" },
                    disabled: isBusy,
                    onClick: () => {
                        setQuantityDialogItem(singleItem);
                    }
                });
            }
        }

        // --- Actions for One or More Selected Subscriptions ---
        if (selectedItems.length > 0) {
            // --- Suspend/Resume Action ---
            const areAllInvoiceBased = selectedItems.every(item => item.method === "invoice" || item.method === "invoice_static" || item.method === "invoice_dynamic");
            const areAllActive = selectedItems.every(item => (item.status as string)?.toLowerCase() === SubscriptionStatusEnum.Active);
            const areAllSuspended = selectedItems.every(item => (item.status as string)?.toLowerCase() === SubscriptionStatusEnum.Suspended);

            if (isGlobalAdmin && areAllInvoiceBased && (areAllActive || areAllSuspended)) {
                items.push({
                    key: "suspend",
                    text: areAllActive ? languageStrings.Suspend : languageStrings.Resume,
                    iconProps: { iconName: areAllActive ? "CircleStop" : "Play" },
                    disabled: isBusy,
                    onClick: () => {
                        setIsBusy(true);
                        updateStatus(selectedItems, areAllActive ? SubscriptionStatusEnum.Suspended : SubscriptionStatusEnum.Active);
                    }
                });
            }

            // --- Cancel Action ---
            const isCancellationDisallowed = selectedItems.some(item => {
                const status = (item.status as string)?.toLowerCase();
                const method = (item.method as string)?.toLowerCase();

                if (status === SubscriptionStatusEnum.Active && item.tier === SubscriptionTierEnum.Trial && isGlobalAdmin) {
                    return false;
                }

                // Stripe keeps status=Active during the pending-cancellation window
                // (between cancel_at_period_end=true and the deletion webhook). The only
                // server-side signal is CancelAt being set; without this guard the Cancel
                // button stays enabled on a row that already shows "Cancels {date}".
                if (method === "stripe" && item.cancelAtRaw) {
                    return true;
                }

                return status !== SubscriptionStatusEnum.Active
                    || (item.method !== "invoice"
                        && item.method !== "invoice_static"
                        && item.method !== "invoice_dynamic"
                        && method !== "stripe");
            });

            if (!isCancellationDisallowed) {
                const hasStripe = selectedItems.some(item => (item.method as string)?.toLowerCase() === "stripe");
                items.push({
                    key: "cancel",
                    text: languageStrings.CancelSubscription,
                    iconProps: { iconName: "Cancel" },
                    disabled: isBusy,
                    onClick: () => {
                        let message: string;
                        if (hasStripe) {
                            const stripeItems = selectedItems.filter(i => (i.method as string)?.toLowerCase() === "stripe");
                            const expiryText = stripeItems[0]?.expiryDateRaw
                                ? new Date(stripeItems[0].expiryDateRaw).toLocaleDateString()
                                : languageStrings.CurrentPeriodEnd;
                            message = languageStrings.CancelStripeSubscriptionMessage.replace('{expiry}', expiryText);
                        } else {
                            message = languageStrings.CancelSubscriptionConfirmMessage;
                        }
                        setCancelDialog({ items: [...selectedItems], message });
                    }
                });
            }
        }

        return items;
        // selectedTenant and tenants are tracked so the closures over assignAll / revokeAll /
        // updateStatus (none of which are wrapped in useCallback) get refreshed when the
        // active tenant scope changes — matches the original commandBarItems dep set.
    }, [selectedItems, isBusy, isGlobalAdmin, languageStrings, selectedTenant, tenants]);

    const globalMenuItems = React.useMemo<ICommandBarItemProps[]>(() => {
        const items: ICommandBarItemProps[] = [];
        const showGlobalCommands = shouldShowSubscriptionGlobalCommands(selectedItems);
        if (!showGlobalCommands) return items;

        // --- Tenant Selection Actions ---
        if (tenants.length > 1) {
            if (selectedTenant) {
                items.push({
                    key: "alltenants",
                    text: `${languageStrings.ShowAll} ${languageStrings.Tenants}`,
                    iconProps: { iconName: "SearchAndApps" },
                    disabled: isBusy,
                    onClick: () => {
                        setFilterValue(null);
                        setCheckoutResult(null);
                        navigate('/subscriptions');
                    }
                });
            }
            items.push({
                key: "tenant",
                text: selectedTenant ? `${languageStrings.Change} ${languageStrings.Tenant}` : languageStrings.SelectTenant,
                iconProps: { iconName: "UserSync" },
                disabled: isBusy,
                onClick: () => setIsSelectTenantPanelOpen(true)
            });
        }

        // --- Subscription Management Actions ---
        // Button visibility is no longer gated on isGlobalAdmin. Non-global-admin
        // portal users land in the panel with Tier=Paid + Method=Stripe defaults
        // (the dropdowns are hidden for them in AddSubscriptionPanel). The backend
        // PortalAdminPolicy on /api/portal/subscriptions/create remains the
        // authoritative gate for who can actually create one.
        if (selectedTenant) {
            items.push({
                key: "add",
                text: languageStrings.AddSubscription,
                iconProps: { iconName: "Add" },
                disabled: !selectedTenant || isBusy,
                onClick: () => setIsAddSubscriptionPanelOpen(true)
            });
        }

        items.push({
            key: "export",
            text: languageStrings.ExportToExcel,
            iconProps: { iconName: "ExcelDocument" },
            onClick: () => { exportFile(); },
            onRenderIcon: isExporting ? () => <Spinner size={SpinnerSize.small} style={{ marginRight: 4, marginLeft: 4 }} /> : undefined
        });

        // Orphan customers: only show the button when there's actually something to fix.
        // Global-admin only.
        if (isGlobalAdmin && orphanCount > 0) {
            items.push({
                key: "orphans",
                text: `${languageStrings.OrphanToolbarButton} (${orphanCount})`,
                iconProps: { iconName: "UserWarning" },
                onClick: () => setIsOrphanPanelOpen(true),
                buttonStyles: { root: { color: '#a4262c' }, icon: { color: '#a4262c' } },
            });
        }

        // Manage Stripe Products: global-admin only. Lets ops add / edit / deactivate Stripe
        // product registrations from the portal instead of editing appsettings + redeploying.
        if (isGlobalAdmin) {
            items.push({
                key: "manage-stripe-products",
                text: languageStrings.ManageStripeProducts ?? "Manage Stripe products",
                iconProps: { iconName: "Shop" },
                onClick: () => setIsManageStripeProductsPanelOpen(true),
            });
        }

        return items;
        // visibleSubscriptions is tracked transitively: exportFile is a useCallback over
        // [selectedTenant, visibleSubscriptions], so when its closed-over rows change we
        // must invalidate this memo so the cached export onClick captures the fresh fn.
    }, [selectedItems, selectedTenant, tenants, isBusy, isExporting, isGlobalAdmin, languageStrings, orphanCount, visibleSubscriptions]);

    const commandBarItems = React.useMemo<ICommandBarItemProps[]>(
        () => [...selectionMenuItems, ...globalMenuItems],
        [selectionMenuItems, globalMenuItems]
    );

    // const exportFile = () => {
    //     setIsExportPanelOpen(true);
    // };
    const exportFile = React.useCallback(async () => {
        setIsExporting(true);
        await new Promise(r => setTimeout(r, 200));
        const ws = XLSX.utils.json_to_sheet(visibleSubscriptions?.map(item => ({
            createdDate: item.createdDateText,
            tenantName: item.tenantName,
            status: item.status,
            interval: item.interval,
            product: item.product,
            description: item.description,
            licenseCount: item.licenseCount?.toString(),
            availableLicenses: item.availableLicenses !== "-" ? item.availableLicenses : "",
            assignedLicenses: item.assignedLicenses !== "-" ? item.assignedLicenses : "",
            expiryDate: item.expiryDateRaw ? `${new Date(item.expiryDateRaw)?.toLocaleDateString()} ${new Date(item.expiryDateRaw)?.toLocaleTimeString()}` : "",
            paymentPlatform: item.paymentPlatform,
            amountString: item.amountString !== "-" ? item.amountString : "",
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, languageStrings.Subscriptions);

        const headerRow = [
            languageStrings.Created,
            languageStrings.Tenant,
            languageStrings.Status,
            languageStrings.Frequency,
            languageStrings.Product,
            languageStrings.Description,
            languageStrings.LicenseCount,
            languageStrings.AvailableLicences,
            languageStrings.AssignedLicences,
            languageStrings.ExpiryDate,
            languageStrings.PaymentPlatform,
            languageStrings.Amount
        ].map(headerText => ({ v: headerText, s: { font: { bold: true } } }));
        XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: "A1" });

        const wscols = [
            { wch: 20 },
            { wch: 25 },
            { wch: 15 },
            { wch: 15 },
            { wch: 20 },
            { wch: 45 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 10 }
        ];
        ws["!cols"] = wscols;
        const filename = selectedTenant?.friendlyName
            ? `Docubird_${languageStrings.Subscriptions}_${selectedTenant.friendlyName}.xlsx`
            : `Docubird_${languageStrings.Subscriptions}.xlsx`;
        XLSX.writeFile(wb, filename, { compression: true });
        setIsExporting(false);
    }, [selectedTenant, visibleSubscriptions]);

    const onSearchFilter = React.useCallback((filterText: string) => {
        setFilterValue(filterText);
    }, []);
    const onChangeFilter = React.useCallback((event?: React.ChangeEvent<HTMLInputElement>, filterText?: string) => {
        setFilterValue(filterText);
    }, []);
    const onClearFilter = React.useCallback(() => {
        setFilterValue(null);
    }, []);
    const onEscapeFilter = React.useCallback(() => {
        setFilterValue(null);
    }, []);

    const onClearSelection = React.useCallback((event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        releaseTransientCommandFocus(event?.currentTarget);
        setSelectedItems([]);
        setDeselectAllSubscriptions(prev => !prev);
    }, []);

    const handleRefresh = React.useCallback(async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        try {
            await refreshSubscriptionsRef.current();
        } finally {
            setIsRefreshing(false);
        }
    }, [isRefreshing]);

    const commandBarFarItems = React.useMemo(() => {
        const items: ICommandBarItemProps[] = [];

        if (shouldShowSubscriptionSelectionCount(selectedItems)) {
            const selectionCount = getSubscriptionSelectionCount(selectedItems);
            const selectedLabel = languageStrings.Selected ?? "selected";
            items.push({
                key: "selected-count",
                text: getSubscriptionSelectionLabel(selectionCount, selectedLabel),
                ariaLabel: languageStrings.ClearSelection,
                iconProps: { iconName: "Cancel" },
                onClick: onClearSelection,
            });
        }

        items.push({
            key: "Search",
            onRender: () =>
                <div className={portalCommandBarSearchContainerStyle}>
                    <SearchBox
                        styles={UserTableStyle.searchBoxStyles}
                        disabled={!visibleSubscriptions?.length}
                        placeholder={languageStrings.Search}
                        value={filterValue ?? ""}
                        onSearch={onSearchFilter}
                        onChange={onChangeFilter}
                        onClear={onClearFilter}
                        onEscape={onEscapeFilter}
                    />
                </div>,
        });

        items.push({
            key: "refresh",
            iconProps: { iconName: "Refresh" },
            iconOnly: true,
            ariaLabel: languageStrings.Refresh,
            tooltipHostProps: {
                content: <span>{languageStrings.Refresh}</span>
            },
            disabled: isRefreshing || isBusy,
            onRenderIcon: isRefreshing
                ? () => <Spinner size={SpinnerSize.xSmall} style={{ marginRight: 4, marginLeft: 4 }} />
                : undefined,
            onClick: () => { handleRefresh(); }
        });

        return items;
    }, [
        filterValue,
        handleRefresh,
        isBusy,
        isRefreshing,
        languageStrings,
        onChangeFilter,
        onClearFilter,
        onClearSelection,
        onEscapeFilter,
        onSearchFilter,
        selectedItems,
        visibleSubscriptions?.length
    ]);

    const revokeAll = async (items: any[]) => {
        try {
            await Promise.all(items.map(async item => {
                await revokeAllUsersFromSubscription(item.id);

                const tenantUsers: ITenantUser[] = users.filter(user => user.subscriptionId === item.id);

                for (let index = 0; index < tenantUsers.length; index++) {
                    const newTenantUser = { ...tenantUsers[index] };
                    newTenantUser.subscriptionId = null;
                    newTenantUser.licenseType = getUserLicenseType(null);
                    newTenantUser.productType = null;
                    updateUser(newTenantUser, true);
                }
            }))
        } catch {

        }

        setIsBusy(false);
        if (selectedTenant) {
            fetchSubscrptionsAndPayments();
        } else if (!selectedTenant && tenants?.length > 1) {
            fetchMultiTenantSubscrptions();
        }
    }

    const assignAll = async (item: any) => {
        try {
            const request: AssignSubscriptionRequest = {
                tenantId: item.tenantId,
                subscriptionId: item.id
            }
            const updatedUserIds = await assignAllUsersToSubscription(request);

            if (Array.isArray(updatedUserIds) && updatedUserIds.length > 0) {
                const tenantUsers: ITenantUser[] = users?.filter(user => updatedUserIds.includes(user.id)) ?? [];

                for (let index = 0; index < tenantUsers.length; index++) {
                    const newTenantUser = { ...tenantUsers[index] };
                    newTenantUser.subscriptionId = item.id;
                    newTenantUser.licenseType = getUserLicenseType(item);
                    newTenantUser.productType = item.product?.trim() ?? null;
                    updateUser(newTenantUser, true);
                }
            }
        } catch (error) {
            console.log("error", error)
        }

        setIsBusy(false);
        if (selectedTenant) {
            fetchSubscrptionsAndPayments();
        } else if (!selectedTenant && tenants?.length > 1) {
            fetchMultiTenantSubscrptions();
        }
    }

    const updateStatus = async (items: any[], status: SubscriptionStatusEnum) => {
        try {
            await Promise.all(items.map(async item => {
                const request: SubscriptionStatusUpdateRequest = {
                    status: status,
                    subscriptionId: item.id
                }

                await updateSubscriptionStatus(request);

                const tenantUsers: ITenantUser[] = users.filter(user => user.subscriptionId === item.id);

                const updatedSubscription = {
                    ...item,
                    status
                }

                for (let index = 0; index < tenantUsers.length; index++) {
                    const newTenantUser = { ...tenantUsers[index] };
                    newTenantUser.licenseType = getUserLicenseType(updatedSubscription);
                    updateUser(newTenantUser, true);
                }
            }))
        } catch (error) {

        }

        setIsBusy(false);
        if (selectedTenant) {
            fetchSubscrptionsAndPayments();
        } else if (!selectedTenant && tenants?.length > 1) {
            fetchMultiTenantSubscrptions();
        }
    }

    const cancelSubscriptions = async (items: any[]) => {
        try {
            // Promise.all rejects on the first failure, but the other in-flight cancels
            // still run to completion. The `finally` below refreshes the list either way
            // so the user sees the actual state of every row — including any that did
            // succeed before another in the batch failed. The rejection then propagates
            // to the caller (the ConfirmDialog) which surfaces it inline.
            await Promise.all(items.map(async item => {
                const isStripe = (item.method as string)?.toLowerCase() === "stripe";
                const clientOperationId = isStripe && typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                    ? crypto.randomUUID()
                    : undefined;
                await cancelSubscription(item.id, clientOperationId);
            }));
        } finally {
            try { await refreshSubscriptionsRef.current(); } catch { /* ignored */ }
        }
    }

    const reactivateOne = async (item: any) => {
        try {
            // Mirrors cancelSubscriptions' finally — even on failure we refresh so the row
            // shows the real server state. The throw propagates to ConfirmDialog which
            // displays it inline; the dialog stays open until the user dismisses or retries.
            const clientOperationId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                ? crypto.randomUUID()
                : undefined;
            await reactivateSubscription(item.id, clientOperationId);
        } finally {
            try { await refreshSubscriptionsRef.current(); } catch { /* ignored */ }
        }
    }

    return (
        <>
            <div style={subscriptionPageStyle}>
                <div style={subscriptionContentStyle}>

                <Label styles={headingStyle}>{`${selectedTenant ? `${languageStrings.Subscriptions} (${selectedTenant.friendlyName})` : languageStrings.Subscriptions}`}</Label>

                {checkoutResult && (
                    <MessageBar
                        messageBarType={checkoutResult === 'success' ? MessageBarType.success : MessageBarType.info}
                        isMultiline={false}
                        onDismiss={() => setCheckoutResult(null)}
                        dismissButtonAriaLabel={languageStrings.Cancel}
                        styles={{ root: { marginBottom: 8 } }}
                    >
                        {checkoutResult === 'success'
                            ? 'Payment received — your subscription will appear here shortly.'
                            : 'Checkout was cancelled. No subscription was created.'}
                    </MessageBar>
                )}

                {isGlobalAdmin && orphanCount > 0 && !orphanBannerDismissed ? (
                    <MessageBar
                        messageBarType={MessageBarType.warning}
                        isMultiline={false}
                        onDismiss={() => setOrphanBannerDismissed(true)}
                        dismissButtonAriaLabel={languageStrings.Cancel}
                        actions={
                            <div>
                                <MessageBarButton onClick={() => setIsOrphanPanelOpen(true)}>
                                    {languageStrings.Review}
                                </MessageBarButton>
                            </div>
                        }
                        styles={{ root: { marginBottom: 8 } }}
                    >
                        {orphanCount === 1
                            ? languageStrings.OrphanBannerSingular
                            : (languageStrings.OrphanBannerPlural || '').replace('{count}', String(orphanCount))}
                    </MessageBar>
                ) : null}

                <CommandBar
                    items={commandBarItems}
                    styles={portalCommandBarStyles}
                    farItems={commandBarFarItems}
                />
                {subscriptions?.length > 0 && (
                    <Pivot
                        selectedKey={subscriptionView}
                        onLinkClick={onSubscriptionViewChange}
                        styles={portalPivotStyles}
                    >
                        <PivotItem
                            itemKey={SubscriptionDisplayView.Current}
                            headerText={`${languageStrings.SubscriptionViewCurrent ?? "Current"} (${subscriptionViewCounts.current})`}
                        />
                        <PivotItem
                            itemKey={SubscriptionDisplayView.Archived}
                            headerText={`${languageStrings.SubscriptionViewArchived ?? "Archived"} (${subscriptionViewCounts.archived})`}
                        />
                        <PivotItem
                            itemKey={SubscriptionDisplayView.All}
                            headerText={`${languageStrings.SubscriptionViewAll ?? "All"} (${subscriptionViewCounts.all})`}
                        />
                    </Pivot>
                )}
                {(selectedTenant || (tenants?.length > 0 && subscriptions?.length > 0)) && <div className={subscriptionTableContainerStyle} id="tableContainer">
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                        <Stack tokens={{ childrenGap: 70 }}>
                            {visibleSubscriptions?.length > 0 &&
                                <SubscriptionsList
                                    items={visibleSubscriptions}
                                    isLoading={isLoading}
                                    itemSelected={(items: any[]) => {
                                        setSelectedItems(items);
                                    }}
                                    deselect={deselectAllSubscriptions}
                                    isMultiTenant={!selectedTenant}
                                    filterText={filterValue}
                                    contextMenuItems={selectionMenuItems}
                                />}
                            {visibleSubscriptions?.length === 0 &&
                                <div style={{ textAlign: "center", paddingTop: 20 }}>
                                    <Label styles={labelHeadingStyle}>
                                        {subscriptions?.length > 0
                                            ? languageStrings.NoSubscriptionsInView ?? languageStrings.NoSubscriptionsFound
                                            : languageStrings.NoSubscriptionsFound}
                                    </Label>
                                </div>}
                        </Stack>
                    </ScrollablePane>
                </div>}
                </div>
            </div>

            {isAddSubscriptionPanelOpen &&
                    <AddSubscriptionPanel
                        isOpen={isAddSubscriptionPanelOpen}
                        closePanel={() => setIsAddSubscriptionPanelOpen(false)}
                        tenant={selectedTenant}
                        existingSubscriptions={subscriptions}
                        onSuccess={() => {
                            fetchSubscrptionsAndPayments();
                        }}
                    />}
            {isSelectTenantPanelOpen &&
                    <SelectTenantPanel
                        isOpen={isSelectTenantPanelOpen}
                        closePanel={() => setIsSelectTenantPanelOpen(false)}
                        onTenantSelected={(t: ITenant) => { setCheckoutResult(null); navigate('/subscriptions/' + t.id); }}
                        tenants={tenants}
                    />}
            {isExportPanelOpen &&
                    <SubscriptionsExportPanel
                        isOpen={isExportPanelOpen}
                        dismissPanel={() => setIsExportPanelOpen(false)}
                    />}
            {isOrphanPanelOpen && (
                    <OrphanCustomersPanel
                        isOpen={isOrphanPanelOpen}
                        onDismiss={() => {
                            setIsOrphanPanelOpen(false);
                            // Refresh the count when the panel closes so the badge / banner
                            // reflect any adoptions made inside the panel even if onCountChanged
                            // missed one.
                            refreshOrphanCount();
                        }}
                        onCountChanged={refreshOrphanCount}
                    />
                )}
            {isManageStripeProductsPanelOpen && (
                    <ManageStripeProductsPanel
                        isOpen={isManageStripeProductsPanelOpen}
                        onDismiss={() => setIsManageStripeProductsPanelOpen(false)}
                        refreshNonce={stripeCatalogNonce}
                    />
                )}
            {quantityDialogItem && (() => {
                    // invoice_static seat changes apply immediately server-side (no Stripe
                    // proration/preview/webhook), so the dialog hides the proration UI and the
                    // confirm path does a direct update + refresh rather than queuing a pending
                    // (webhook-applied) change.
                    const isInvoiceQuantity = (quantityDialogItem.method as string)?.toLowerCase() === "invoice_static";
                    return (
                        <ChangeQuantityDialog
                            isOpen={!!quantityDialogItem}
                            currentQuantity={typeof quantityDialogItem.licenseCount === "number" ? quantityDialogItem.licenseCount : 1}
                            assignedCount={typeof quantityDialogItem.assignedLicenseCount === "number" ? quantityDialogItem.assignedLicenseCount : 0}
                            fetchPreview={isInvoiceQuantity ? undefined : (qty, behaviour) => previewStripeSubscriptionQuantity(quantityDialogItem.id, qty, behaviour)}
                            supportsProration={!isInvoiceQuantity}
                            autoAssignOnGrow={!isInvoiceQuantity}
                            revokeMessageTemplate={isInvoiceQuantity ? languageStrings.AutoRevokeMessageImmediate : undefined}
                            submittedMessage={isInvoiceQuantity ? languageStrings.QuantityChangeApplied : undefined}
                            onConfirm={async (newQty, prorate) => {
                                if (isInvoiceQuantity) {
                                    await updateInvoiceSubscriptionQuantity(quantityDialogItem.id, newQty);
                                    await refreshSubscriptionsRef.current();
                                } else {
                                    await updateStripeSubscriptionQuantity(
                                        quantityDialogItem.id,
                                        newQty,
                                        prorate ? "create_prorations" : "none"
                                    );
                                    addPendingQuantityChange(quantityDialogItem.id, newQty);
                                }
                            }}
                            onDismiss={() => setQuantityDialogItem(null)}
                        />
                    );
                })()}
            {historyPanelSubId != null && (
                    <SubscriptionHistoryPanel
                        isOpen={historyPanelSubId != null}
                        subscriptionId={historyPanelSubId}
                        onDismiss={() => setHistoryPanelSubId(null)}
                    />
                )}
            {paymentsPanelSubId != null && (
                    <SubscriptionPaymentsPanel
                        isOpen={paymentsPanelSubId != null}
                        subscriptionId={paymentsPanelSubId}
                        onDismiss={() => setPaymentsPanelSubId(null)}
                    />
                )}
            {cancelDialog && (
                    <ConfirmDialog
                        isOpen={!!cancelDialog}
                        title={languageStrings.CancelSubscriptionConfirmTitle}
                        message={cancelDialog.message}
                        confirmText={languageStrings.CancelSubscription}
                        onConfirm={async () => {
                            // The dialog stays open and shows its spinner while we work.
                            // On success we close; on failure the dialog catches and
                            // displays the error inline, leaving the user to retry or
                            // dismiss.
                            await cancelSubscriptions(cancelDialog.items);
                            setCancelDialog(null);
                        }}
                        onDismiss={() => setCancelDialog(null)}
                    />
                )}
            {reactivateDialog && (
                    <ConfirmDialog
                        isOpen={!!reactivateDialog}
                        title={languageStrings.ReactivateSubscriptionConfirmTitle}
                        message={reactivateDialog.message}
                        confirmText={languageStrings.ReactivateSubscription}
                        onConfirm={async () => {
                            await reactivateOne(reactivateDialog.item);
                            setReactivateDialog(null);
                        }}
                        onDismiss={() => setReactivateDialog(null)}
                    />
                )}
        </>

    )
}

export default connector(Subscriptions);
